import { supabase } from '@/lib/supabase'
import type { CreateMixRequest, UpdateMixRequest, ApiError, Recipe, DeleteMixRequest, Mix, Ingredient } from '@/types'

/**
 * Mix Service
 * Provides methods to interact with mixes via Supabase
 */
function createMixService() {
  /**
   * Create a new mix via the mix-create Edge Function.
   */
  const createMix = async (data: CreateMixRequest): Promise<Mix> => {
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user
    if (!user?.id) {
      const apiError: ApiError = { message: 'Not authenticated', status: 401 }
      throw apiError
    }

    const body: Record<string, unknown> = {
      name: data.name,
      type: data.type,
    }

    if (data.type === 'schedule') {
      body.started_at = data.startedAt
      body.number_of_days = data.numberOfDays
      if (data.mealSlotOrder) body.meal_slot_order = data.mealSlotOrder
    }

    const { data: response, error } = await supabase.functions.invoke('mix-create', {
      body: JSON.stringify(body),
    })

    if (error) {
      const apiError: ApiError = { message: error.message, status: 500 }
      throw apiError
    }

    if (!response?.data) {
      const apiError: ApiError = { message: 'Failed to create mix', status: 500 }
      throw apiError
    }

    return response.data as Mix
  }

  /**
   * Update an existing mix
   */
  const updateMix = async (id: string, request: UpdateMixRequest): Promise<void> => {
    const updateData: Record<string, unknown> = {}
    if (request.name !== undefined) updateData.name = request.name
    if (request.recipes !== undefined) updateData.recipes = request.recipes
    if (request.ingredients !== undefined) updateData.ingredients = request.ingredients

    const { error } = await supabase
      .from('mixes')
      .update(updateData)
      .eq('id', id)

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  /**
   * Delete a mix
   */
  const deleteMix = async (mixId: string, request: DeleteMixRequest): Promise<void> => {
    const { error } = await supabase
      .from('mixes')
      .update({ archived_at: request.archivedAt })
      .eq('id', mixId)

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  /**
   * Create a new recipe within a mix
   *
   * Fast path (dedup): checks the `recipes` table for an existing URL
   * and appends directly if found. Falls back to Edge Function for
   * new URLs that need Ollama extraction.
   */
  const createRecipe = async (mixId: string, url: string): Promise<void> => {
    // 1. Check if already in this mix
    const { data: mix } = await supabase
      .from('mixes')
      .select('recipes')
      .eq('id', mixId)
      .single()

    if (mix?.recipes?.some((r: Recipe) => r.url === url)) {
      const apiError: ApiError = {
        message: 'Recipe already in mix',
        status: 409,
      }
      throw apiError
    }

    // 2. Check global recipes table for URL dedup
    const { data: existingRecipe } = await supabase
      .from('recipes')
      .select('id, author, name, url, image, instructions, ingredient_ids')
      .eq('url', url)
      .maybeSingle()

    // Get current user for sharer info
    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id
    if (!userId) {
      const apiError: ApiError = { message: 'Not authenticated', status: 401 }
      throw apiError
    }

    const userName = (session?.user?.user_metadata?.full_name as string) || session?.user?.email || 'User'
    const userImage = (session?.user?.user_metadata?.avatar_url as string) || ''
    const nowIso = new Date().toISOString()

    if (existingRecipe) {
      // Fast path: fetch ingredients and append atomically
      const { data: ingredients } = await supabase
        .from('ingredients')
        .select('id, name, quantity, unit, category, recipe_name, full_text')
        .in('id', existingRecipe.ingredient_ids)

      const sharedRecipe = {
        id: existingRecipe.id,
        author: existingRecipe.author || null,
        name: existingRecipe.name,
        url: existingRecipe.url,
        image: existingRecipe.image || null,
        instructions: existingRecipe.instructions || '',
        ingredientIds: existingRecipe.ingredient_ids,
        selected: true,
        selectedBy: userId,
        selectedByName: userName,
        sharerId: userId,
        sharerName: userName,
        sharerImage: userImage || null,
        createdAt: nowIso,
        updatedAt: nowIso,
      }

      const sharedIngredients = (ingredients || []).map((ing: Record<string, unknown>) => ({
        id: ing.id,
        name: ing.name,
        quantity: ing.quantity || null,
        unit: ing.unit || null,
        category: ing.category || null,
        recipeName: ing.recipe_name || null,
        fullText: ing.full_text,
        selected: true,
        selectedBy: userId,
        selectedByName: userName,
        sharerId: userId,
        sharerName: userName,
        sharerImage: userImage || null,
        sourceType: ing.recipe_name ? 'RECIPE' : 'STANDALONE',
        createdAt: nowIso,
        updatedAt: nowIso,
      }))

      const { error } = await supabase.rpc('append_recipe_to_mix', {
        p_mix_id: mixId,
        p_recipe: sharedRecipe,
        p_ingredients: sharedIngredients,
      })

      if (error) {
        const apiError: ApiError = {
          message: error.message,
          status: 500,
        }
        throw apiError
      }

      return
    }

    // Slow path: call Edge Function for new URLs
    const { error } = await supabase.functions.invoke('recipe-create', {
      body: JSON.stringify({
        mix_id: mixId,
        url,
        requester_name: userName,
        requester_image: userImage,
      }),
    })

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  /**
   * Set the selection for a recipe
   */
  /**
   * Append an existing global recipe to a mix by recipe ID.
   * Used by the batch-add flow in the recipe discovery page.
   */
  const appendRecipeToMix = async (
    mixId: string,
    recipeId: string,
    slotContext?: { scheduledAt: string; mealSlotId: string; userId?: string; userName?: string; userImage?: string }
  ): Promise<void> => {
    // Use provided user info or fallback to session
    const sessionUser = slotContext?.userId
      ? null
      : (await supabase.auth.getSession()).data?.session?.user
    const userId = slotContext?.userId || sessionUser?.id
    if (!userId) {
      const apiError: ApiError = { message: 'Not authenticated', status: 401 }
      throw apiError
    }

    const userName =
      slotContext?.userName ||
      (sessionUser?.user_metadata?.full_name as string) ||
      sessionUser?.email ||
      'User'
    const userImage = slotContext?.userImage || (sessionUser?.user_metadata?.avatar_url as string) || ''
    const nowIso = new Date().toISOString()

    // Fetch recipe from global pool
    const { data: recipe } = await supabase
      .from('recipes')
      .select('id, author, name, url, image, instructions, ingredient_ids')
      .eq('id', recipeId)
      .single()

    if (!recipe) {
      const apiError: ApiError = { message: `Recipe ${recipeId} not found`, status: 404 }
      throw apiError
    }

    // Fetch ingredients
    const { data: ingredients } = await supabase
      .from('ingredients')
      .select('id, name, quantity, unit, category, recipe_name, full_text')
      .in('id', recipe.ingredient_ids)

    const selected = true
    const scheduledAt = slotContext?.scheduledAt || null
    const mealSlotId = slotContext?.mealSlotId || null

    const sharedRecipe = {
      id: recipe.id,
      author: recipe.author || null,
      name: recipe.name,
      url: recipe.url,
      image: recipe.image || null,
      instructions: recipe.instructions || '',
      ingredientIds: recipe.ingredient_ids,
      selected,
      selectedBy: userId,
      selectedByName: userName,
      sharerId: userId,
      sharerName: userName,
      sharerImage: userImage || null,
      scheduledAt,
      mealSlotId,
      createdAt: nowIso,
      updatedAt: nowIso,
    }

    const sharedIngredients = (ingredients || []).map((ing: Record<string, unknown>) => ({
      id: ing.id,
      name: ing.name,
      quantity: ing.quantity || null,
      unit: ing.unit || null,
      category: ing.category || null,
      recipeName: ing.recipe_name || null,
      fullText: ing.full_text,
      selected: true,
      selectedBy: userId,
      selectedByName: userName,
      sharerId: userId,
      sharerName: userName,
      sharerImage: userImage || null,
      sourceType: ing.recipe_name ? 'RECIPE' : 'STANDALONE' as 'RECIPE' | 'STANDALONE',
      scheduledAt,
      mealSlotId,
      createdAt: nowIso,
      updatedAt: nowIso,
    }))

    const { error } = await supabase.rpc('append_recipe_to_mix', {
      p_mix_id: mixId,
      p_recipe: sharedRecipe,
      p_ingredients: sharedIngredients,
    })

    if (error) {
      const apiError: ApiError = { message: error.message, status: 500 }
      throw apiError
    }
  }

  /**
   * Update recipe selection state within a mix.
   * When ingredients are provided, both arrays are updated in a single
   * Supabase call so Realtime delivers a unified payload with both
   * recipes and ingredients (prevents partial-payload races).
   */
  const selectRecipe = async (mixId: string, recipes: Recipe[], ingredients?: Ingredient[]): Promise<void> => {
    const updateData: Record<string, unknown> = { recipes }
    if (ingredients !== undefined) updateData.ingredients = ingredients

    const { error } = await supabase
      .from('mixes')
      .update(updateData)
      .eq('id', mixId)

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  /**
   * Delete a recipe from a mix
   */
  const deleteRecipe = async (mixId: string, recipes: Recipe[]): Promise<void> => {
    const { error } = await supabase
      .from('mixes')
      .update({ recipes })
      .eq('id', mixId)

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  /**
   * Toggle favorite on a recipe within a mix
   */
  const favoriteRecipe = async (mixId: string, recipeId: string, action: 'add' | 'remove'): Promise<void> => {
    const { error } = await supabase.functions.invoke('recipe-favorite', {
      body: JSON.stringify({ mix_id: mixId, recipe_id: recipeId, action }),
    })

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  return {
    createMix,
    updateMix,
    deleteMix,
    createRecipe,
    appendRecipeToMix,
    selectRecipe,
    deleteRecipe,
    favoriteRecipe,
  }
}

export const mixService = createMixService()
